import { useEffect } from "react";

/**
 * Phase 36cg — Global "double-Escape" closes the topmost modal.
 *
 * Behaviour: pressing Escape twice within 500ms closes the most recently
 * opened modal (LIFO).  A single Escape is intentionally ignored so the
 * user doesn't lose unsaved input by accidentally tapping Esc once.
 *
 * Implementation uses a module-level stack of onClose callbacks.  Each
 * mounted modal pushes its callback on mount and pops on unmount.  The
 * global keydown listener fires the TOP callback only — so if a modal
 * opens another modal on top, the top one closes first, then the parent.
 *
 * Usage in a custom modal that uses createPortal directly:
 *
 *   import useDoubleEscapeClose from "../hooks/useDoubleEscapeClose";
 *   function MyModal({ onClose }) {
 *     useDoubleEscapeClose(onClose);   // ← one line, anywhere in the body
 *     return createPortal(<div>...</div>, document.body);
 *   }
 *
 * Already baked into the shared Modal.jsx component, so any modal using
 * the shared component gets this for free with no changes.
 */

// Stack of onClose callbacks for currently-open modals (top = most recent)
const stack = [];

// Install the global listener exactly once per page load.  The flag on
// window prevents double-installation if the bundle is hot-reloaded in
// development.
if (typeof window !== "undefined" && !window.__doubleEscInstalled) {
  window.__doubleEscInstalled = true;
  let lastEscAt = 0;
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (stack.length === 0) return;
    const now = Date.now();
    // Two Escapes within 500ms = close intent
    if (now - lastEscAt < 500) {
      const top = stack[stack.length - 1];
      lastEscAt = 0;
      // Defer the close so the keydown handler returns first; some
      // modals do focus restoration that conflicts with synchronous
      // unmount.
      setTimeout(() => {
        try { top && top(); } catch (err) { /* swallow — modal might already be unmounting */ }
      }, 0);
    } else {
      lastEscAt = now;
    }
  });
}

/**
 * @param {Function} onClose  - the modal's close callback
 * @param {boolean}  enabled  - set false to opt out (defaults to true)
 */
export default function useDoubleEscapeClose(onClose, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof onClose !== "function") return;
    stack.push(onClose);
    return () => {
      // Remove THIS callback (not just pop) to handle any out-of-order
      // unmounts cleanly.
      const idx = stack.lastIndexOf(onClose);
      if (idx !== -1) stack.splice(idx, 1);
    };
  }, [onClose, enabled]);
}